import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowLeftIcon, Loader2Icon, MailIcon } from "lucide-react";
import LOGO from "assets/logo.svg";
import { toast } from "utils/toast";

import { forgotPassword } from "../../../api/accounts";

import { Button } from "js/components/ui/button";
import { EmailInput, EmailInputRef } from "js/components/ui/email-input";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);

  const emailInputRef = useRef<EmailInputRef>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate email before submitting
    const isValid = await emailInputRef.current?.validate();
    if (!isValid) {
      return;
    }

    setLoading(true);
    try {
      await forgotPassword({ email: email.toLowerCase() });
      setEmailSent(true);
      toast.success(
        "Si el correo está registrado, recibirás las instrucciones."
      );
    } catch {
      toast.info(
        "Si el correo es correcto, recibirás un correo con las instrucciones."
      );
      setEmailSent(true);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="space-y-8">
        {/* Logo - visible only on mobile */}
        <div className="flex justify-start lg:hidden">
          <a href="https://multiburo.com.mx/">
            <img src={LOGO} alt="Multiburo" className="h-14 w-auto" />
          </a>
        </div>

        {/* Icon */}
        <div className="flex justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <MailIcon className="h-10 w-10 text-primary" />
          </div>
        </div>

        {/* Header */}
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Revisa tu correo
          </h1>
          <p className="text-muted-foreground">
            Hemos enviado las instrucciones para restablecer tu contraseña a{" "}
            <span className="font-medium text-foreground sentry-mask">
              {email}
            </span>
          </p>
        </div>

        {/* Info */}
        <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <p>
            Si no recibes el correo en unos minutos, revisa tu carpeta de
            spam o correo no deseado.
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={() => navigate("/login")}
            className="h-11 w-full text-base font-semibold"
          >
            Volver al inicio de sesión
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setEmailSent(false);
              setEmail("");
              emailInputRef.current?.clear();
            }}
            className="h-11 w-full"
          >
            Usar otro correo
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Logo - visible only on mobile */}
      <div className="flex justify-start lg:hidden">
        <img src={LOGO} alt="Multiburo" className="h-14 w-auto" />
      </div>

      {/* Back Button */}
      <Button
        variant="ghost"
        className="gap-2 pl-0 text-muted-foreground hover:text-foreground"
        onClick={() => navigate("/login")}
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Volver al inicio de sesión
      </Button>

      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          ¿Olvidaste tu contraseña?
        </h1>
        <p className="text-muted-foreground">
          No te preocupes, ingresa el correo con el que te registraste y te
          enviaremos instrucciones para restablecer tu contraseña.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        <EmailInput
          ref={emailInputRef}
          value={email}
          onChange={setEmail}
          onValidationChange={(isValid) => setIsEmailValid(isValid)}
          label="Correo electrónico"
          placeholder="tu@correo.com"
          required
          showIcon={false}
          showStatus={false}
          className="h-11"
        />

        <Button
          type="submit"
          className="h-11 w-full text-base font-semibold"
          disabled={loading || !email}
        >
          {loading ? (
            <>
              <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              Enviando...
            </>
          ) : (
            "Enviar instrucciones"
          )}
        </Button>
      </form>

      {/* Sign Up Link */}
      <p className="text-center text-sm text-muted-foreground">
        ¿Aún no tienes cuenta?{" "}
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 font-semibold"
          onClick={() => navigate("/signup")}
        >
          Regístrate
        </Button>
      </p>
    </div>
  );
};

export default ForgotPassword;
